import {
  Box,
  Button,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useLookup } from 'hooks';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { ProjectGroup } from 'library/types';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useState } from 'react';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

export type UpdateProjectFormParams = {
  groupId: number | null;
  title: string;
};

const defaultValues: UpdateProjectFormParams = {
  groupId: null,
  title: '',
};

type UpdateProjectDialogProps = {
  open: boolean;
  projectId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateProjectDialog = ({ open, projectId, handleClose: closeDialog, refresh }: UpdateProjectDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    items: projectGroups,
    loading: loadingProjectGroups,
    loaded: projectGroupsLoaded,
  } = useLookup(apiService.getProjectGroups, { enabled: open });

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [selectedProjectGroup, setSelectedProjectGroup] = useState<ProjectGroup | null>(null);

  // form validations & form state
  const schema = yup.object().shape({
    title: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    groupId: yup
      .number()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateProjectFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: UpdateProjectFormParams) => {
    const postData = {
      ...data,
      id: projectId as number,
    };

    try {
      setLoading(true);

      const response = await apiService.updateProject(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="projects.updateProjectCompleted"
            defaultMessage="Ürün düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getProjectInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificProject(projectId as number);

      if (response.isSuccess) {
        setValue('groupId', response.data.groupId);
        setValue('title', response.data.title);

        const projectGroup = projectGroups.find((projectGroup) => projectGroup.id === response.data.groupId);

        if (projectGroup) {
          setSelectedProjectGroup(projectGroup);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [projectId, projectGroups, setValue]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedProjectGroup(null);
  };

  // useEffect hooks
  useEffect(() => {
    if (open && projectId !== undefined && projectId !== null && projectGroupsLoaded) {
      getProjectInfos();
    }
  }, [open, projectId, projectGroupsLoaded, getProjectInfos]);

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="projects.updateProject" defaultMessage="Ürün Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="projects.updateProjectText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili ürün için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                options={projectGroups}
                loading={loadingProjectGroups}
                value={selectedProjectGroup}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: ProjectGroup | null) => {
                  setSelectedProjectGroup(newValue);

                  setValue('groupId', newValue ? newValue.id : null);
                }}
                getOptionLabel={(option: ProjectGroup) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                    label={<FormattedMessage id="projects.groupTitle" defaultMessage="Ürün Grubu" />}
                    error={!!errors.groupId}
                    helperText={errors.groupId?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProjectGroups ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="title"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="projects.title" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateProjectDialog;
